import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getReglasCobro, deleteReglaCobro,
} from "../../controllers/api_billing";
import { encryptText } from "../../controllers/api";
import ModalBillingRules from "./ModalBillingRules";

const fmtMonto = (val) => {
  try {
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return n.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch { return String(val); }
};

export default function ListBillingRules() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [viewItem, setViewItem] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado !== "") params.estado = filtroEstado;
      const data = await getReglasCobro(params);
      setRows(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) {
      toast.error("Error al cargar reglas de cobro.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [filtroEstado]);

  const onEdit = (id) => {
    const hash = encryptText(id.toString());
    navigate(`/billing-rules/edit?id=${encodeURIComponent(hash)}`);
  };
  const onCreate = () => navigate("/billing-rules/create");
  const onView = (row) => setViewItem(row);
  const onDeactivate = async (row) => {
    if (!window.confirm(`¿Desactivar regla "${row.nombre}"? (Soft delete: se preservan los datos historicos)`)) return;
    try {
      await deleteReglaCobro(row.id);
      toast.success("Regla desactivada.");
      cargar();
    } catch {
      toast.error("Error al desactivar.");
    }
  };

  return (
    <div className="mx-auto w-full">
      <ToastContainer />
      <div className="bg-white rounded-lg shadow-lg">
        <div className="px-6 py-6 flex justify-between items-center border-b flex-wrap gap-3">
          <h6 className="text-blueGray-700 text-xl font-bold">Reglas de cobro</h6>
          <div className="flex items-center gap-2">
            <label className="text-xs text-blueGray-500">Estado:</label>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
            <button
              onClick={onCreate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 px-4 rounded shadow"
            >
              <i className="fas fa-plus mr-1"></i> Nueva regla
            </button>
          </div>
        </div>
        <div className="p-4 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-blueGray-400">Cargando...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-blueGray-400">
              Sin reglas registradas. Cree la primera regla para iniciar.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-blueGray-50">
                <tr className="text-blueGray-600">
                  <th className="text-left py-2 px-3">Nombre</th>
                  <th className="text-right py-2 px-3">Volumen umbral</th>
                  <th className="text-left py-2 px-3">Regla</th>
                  <th className="text-right py-2 px-3">Monto</th>
                  <th className="text-left py-2 px-3">Moneda</th>
                  <th className="text-center py-2 px-3">Estado</th>
                  <th className="text-center py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-blueGray-50/50">
                    <td className="py-2 px-3 font-semibold">{r.nombre}</td>
                    <td className="py-2 px-3 text-right">{r.volumen}</td>
                    <td className="py-2 px-3">
                      {r.regla === "lote" ? (
                        <span className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">Lote</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-semibold bg-sky-100 text-sky-700">Por factura</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">{fmtMonto(r.monto)}</td>
                    <td className="py-2 px-3">{r.moneda?.codigo || "—"}</td>
                    <td className="py-2 px-3 text-center">
                      {r.estado ? (
                        <span className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700"><i className="fas fa-check-circle"></i> Activa</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-semibold bg-red-100 text-red-600"><i className="fas fa-ban"></i> Inactiva</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => onView(r)}
                        title="Ver detalles"
                        className="text-gray-600 hover:text-gray-800 px-2"
                      ><i className="fas fa-expand"></i></button>
                      <button
                        onClick={() => onEdit(r.id)}
                        title="Editar"
                        className="text-blue-600 hover:text-blue-800 px-2"
                      ><i className="fas fa-pen"></i></button>
                      {r.estado && (
                        <button
                          onClick={() => onDeactivate(r)}
                          title="Desactivar"
                          className="text-red-500 hover:text-red-700 px-2"
                        ><i className="fas fa-ban"></i></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {viewItem && (
        <ModalBillingRules
          message={viewItem}
          isOpen={!!viewItem}
          onClose={() => setViewItem(null)}
        />
      )}
    </div>
  );
}
